import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  Autocomplete,
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  TablePagination,
  InputAdornment,
  Skeleton,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  CloudUpload,
  Delete,
  Edit,
  Add,
  Search,
  FilePresent,
  DeleteSweep,
  Download,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { makeApiCall, getAuthHeaders } from '../utils/apiConfig';
import { apiService, type Product } from '../services/api';
import { validateUpc } from '../utils/inputValidators';
import { PageContainer, PageHeader, EmptyState, LoadingState, CsvTemplateHint } from '../components/ui';
import { ProductsCsvExportButton } from '../components/ProductsCsvExportButton';
import { useAppTranslation } from '../i18n/useAppTranslation';
import { templateColumns as buildTemplateColumns } from '../utils/csvTemplate';
import { SKU_OVERRIDES_TEMPLATE } from '../constants/csvTemplates';
import { useIsMobile } from '../hooks/useIsMobile';
import { tokens } from '../theme/tokens';
import { useOrgFeatureFlag } from '../hooks/useOrgFeatureFlags';
import { SkuCrosswalkPanel } from '../components/skuCrosswalk/SkuCrosswalkPanel';

/** Small uppercase faint label above a mobile card's value, matching the ResponsiveRows mobile cell label. */
const mobileLabelSx = {
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase' as const,
  letterSpacing: '.06em',
  color: tokens.ink.faint,
  display: 'block',
  mb: '4px',
};

interface CatalogHit {
  id: string;
  name: string;
  brand?: string;
  sku?: string;
  upc?: string;
}

const catalogHitFromProduct = (product: Product): CatalogHit => ({
  id: product.id,
  name: product.name || product.model || product.id,
  brand: product.brand,
  sku: product.sku || product.mpn,
  upc: product.upc || product.ean || product.gtin || product.jan,
});

const catalogLabel = (product: CatalogHit): string =>
  [product.brand, product.name, product.sku, product.upc].filter(Boolean).join(' · ');

interface SkuOverride {
  id: string;
  organizationId: string;
  matchKey: string;
  upc: string;
  matchSku?: string;
  storeSku?: string;
  storeBarcode?: string;
  productId?: string;
  productName?: string;
  canonicalSku?: string;
  createdAt: string;
  updatedAt: string;
}

// Columns the server reads: `skuOverrideService.parseCsv` (`MATCH_UPC_HEADERS`, `MATCH_SKU_HEADERS`,
// `CUSTOM_SKU_HEADERS`, `CUSTOM_BARCODE_HEADERS`, `NAME_HEADERS`). A row needs one match value
// (upc or canonical_sku) and one value to write (custom_sku or custom_barcode).
const SKU_OVERRIDE_REQUIRED: Record<string, boolean | 'one-of'> = {
  upc: 'one-of',
  canonical_sku: 'one-of',
  custom_sku: 'one-of',
  custom_barcode: 'one-of',
};

async function apiErrorMessage(response: Response, fallback: string): Promise<string> {
  const body = await response.json().catch(() => null) as { error?: string | { message?: string } } | null;
  if (!body) return fallback;
  if (typeof body.error === 'string' && body.error) return body.error;
  if (body.error && typeof body.error === 'object' && body.error.message) return body.error.message;
  return fallback;
}

interface ImportResult {
  total: number;
  created: number;
  updated: number;
  skipped: number;
  /** Rows that named a product but set no custom SKU or barcode (a partly filled "my products" sheet). */
  blank?: number;
  errors: Array<{ row: number; upc?: string; error: string }>;
}

interface PreviewResult {
  totalRows: number;
  errors: Array<{ row: number; error: string }>;
  blank?: number;
  preview: Array<{ upc?: string; matchSku?: string; storeSku?: string; storeBarcode?: string; productName?: string }>;
}

const LegacySkuOverridesPage: React.FC = () => {
  const { t } = useAppTranslation();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const fullScreenDialog = useMediaQuery(theme.breakpoints.down('sm'));
  const isMobile = useIsMobile();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editUpc, setEditUpc] = useState('');
  const [upcTouched, setUpcTouched] = useState(false);
  const [editMatchSku, setEditMatchSku] = useState('');
  const [editStoreSku, setEditStoreSku] = useState('');
  const [editStoreBarcode, setEditStoreBarcode] = useState('');
  const [editProductName, setEditProductName] = useState('');
  const [editingExisting, setEditingExisting] = useState(false);
  const [catalogQuery, setCatalogQuery] = useState('');
  const [catalogSearch, setCatalogSearch] = useState('');
  const [matchedProduct, setMatchedProduct] = useState<CatalogHit | null>(null);

  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importPreview, setImportPreview] = useState<PreviewResult | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [importLoading, setImportLoading] = useState(false);

  const [deleteAllDialogOpen, setDeleteAllDialogOpen] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['skuOverrides', page, rowsPerPage, search],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const params = new URLSearchParams({
        limit: String(rowsPerPage),
        offset: String(page * rowsPerPage),
        ...(search ? { search } : {}),
      });
      const response = await makeApiCall(`/store/sku-overrides?${params}`, { headers });
      if (!response.ok) throw new Error(await apiErrorMessage(response, 'Failed to load SKU mappings'));
      return response.json() as Promise<{ overrides: SkuOverride[]; total: number }>;
    },
  });

  const upsertMutation = useMutation({
    mutationFn: async (payload: {
      upc?: string;
      matchSku?: string;
      storeSku?: string;
      storeBarcode?: string;
      productName?: string;
    }) => {
      const headers = await getAuthHeaders();
      const response = await makeApiCall('/store/sku-overrides', {
        method: 'PUT',
        headers,
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error(await apiErrorMessage(response, 'Failed to save SKU mapping'));
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['skuOverrides'] });
      setEditDialogOpen(false);
      resetEditForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (matchKey: string) => {
      const headers = await getAuthHeaders();
      const response = await makeApiCall(`/store/sku-overrides/${encodeURIComponent(matchKey)}`, {
        method: 'DELETE',
        headers,
      });
      if (!response.ok) throw new Error(await apiErrorMessage(response, 'Failed to delete SKU mapping'));
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['skuOverrides'] });
    },
  });

  const deleteAllMutation = useMutation({
    mutationFn: async () => {
      const headers = await getAuthHeaders();
      const response = await makeApiCall('/store/sku-overrides/all', {
        method: 'DELETE',
        headers,
      });
      if (!response.ok) throw new Error('Failed to delete all SKU mappings');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['skuOverrides'] });
      setDeleteAllDialogOpen(false);
    },
  });

  const resetCatalogSearch = () => {
    setCatalogQuery('');
    setCatalogSearch('');
    setMatchedProduct(null);
  };

  const resetEditForm = () => {
    setEditUpc('');
    setUpcTouched(false);
    setEditMatchSku('');
    setEditStoreSku('');
    setEditStoreBarcode('');
    setEditProductName('');
    setEditingExisting(false);
    resetCatalogSearch();
  };

  useEffect(() => {
    const handle = window.setTimeout(() => setCatalogSearch(catalogQuery.trim()), 300);
    return () => window.clearTimeout(handle);
  }, [catalogQuery]);

  const { data: catalogHits, isFetching: catalogLoading } = useQuery({
    queryKey: ['skuOverrideCatalogSearch', catalogSearch],
    enabled: editDialogOpen && !editingExisting && catalogSearch.length >= 2,
    queryFn: async () => {
      const result = await apiService.getStoreProducts(20, 0, { search: catalogSearch });
      return (result.data || []).map(catalogHitFromProduct);
    },
  });

  const catalogOptions = useMemo(() => {
    const hits = catalogHits || [];
    return matchedProduct && !hits.some((hit) => hit.id === matchedProduct.id)
      ? [matchedProduct, ...hits]
      : hits;
  }, [catalogHits, matchedProduct]);

  const applyCatalogProduct = (product: CatalogHit | null) => {
    setMatchedProduct(product);
    if (!product) return;
    setEditUpc(product.upc || '');
    // A barcode the catalog filled in was not typed, so show its check-digit problem right away instead of a silently disabled Add.
    setUpcTouched(Boolean(product.upc));
    setEditMatchSku(product.sku || '');
    setEditProductName(product.name);
  };

  const openAddDialog = () => {
    resetEditForm();
    setEditingExisting(false);
    setEditDialogOpen(true);
  };

  const openEditDialog = (override: SkuOverride) => {
    setEditUpc(override.upc);
    setUpcTouched(false);
    setEditMatchSku(override.matchSku || '');
    setEditStoreSku(override.storeSku || '');
    setEditStoreBarcode(override.storeBarcode || '');
    setEditProductName(override.productName || '');
    setEditingExisting(true);
    setEditDialogOpen(true);
  };

  // The check is computed from the current value so save can enforce it; `upcTouched` only decides when the message shows.
  const currentUpcProblem = !editingExisting ? validateUpc(editUpc) : null;
  const upcProblem = upcTouched ? currentUpcProblem : null;

  const handleSave = () => {
    if (currentUpcProblem) {
      setUpcTouched(true);
      return;
    }
    if ((!editUpc.trim() && !editMatchSku.trim()) || (!editStoreSku.trim() && !editStoreBarcode.trim())) return;
    upsertMutation.mutate({
      upc: editUpc.trim(),
      ...(editMatchSku.trim() ? { matchSku: editMatchSku.trim() } : {}),
      storeSku: editStoreSku.trim(),
      storeBarcode: editStoreBarcode.trim(),
      ...(editProductName.trim() ? { productName: editProductName.trim() } : {}),
    });
  };

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportFile(file);
    setImportResult(null);
    setImportLoading(true);

    try {
      const headers = await getAuthHeaders();
      delete (headers as any)['Content-Type'];
      const formData = new FormData();
      formData.append('file', file);
      const response = await makeApiCall('/store/sku-overrides/preview', {
        method: 'POST',
        headers,
        body: formData,
      });
      if (!response.ok) throw new Error(await apiErrorMessage(response, 'Failed to preview CSV'));
      const preview = await response.json();
      setImportPreview(preview);
    } catch (err: any) {
      setImportPreview(null);
      setImportResult({ total: 0, created: 0, updated: 0, skipped: 0, errors: [{ row: 0, error: err.message }] });
    } finally {
      setImportLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
    setImportDialogOpen(true);
  }, []);

  const handleImportConfirm = async () => {
    if (!importFile) return;
    setImportLoading(true);
    try {
      const headers = await getAuthHeaders();
      delete (headers as any)['Content-Type'];
      const formData = new FormData();
      formData.append('file', importFile);
      const response = await makeApiCall('/store/sku-overrides/import', {
        method: 'POST',
        headers,
        body: formData,
      });
      if (!response.ok) throw new Error(await apiErrorMessage(response, 'Failed to import CSV'));
      const result = await response.json();
      setImportResult(result);
      setImportPreview(null);
      queryClient.invalidateQueries({ queryKey: ['skuOverrides'] });
    } catch (err: any) {
      setImportResult({ total: 0, created: 0, updated: 0, skipped: 0, errors: [{ row: 0, error: err.message }] });
    } finally {
      setImportLoading(false);
    }
  };

  const handleExportCsv = useCallback(() => {
    if (!data?.overrides?.length) return;
    const header = 'upc,canonical_sku,custom_sku,custom_barcode,product_name';
    const cell = (value: string) => `"${value.replace(/"/g, '""')}"`;
    const rows = data.overrides.map(
      (o) => [o.upc, o.matchSku || '', o.storeSku || '', o.storeBarcode || '', o.productName || ''].map(cell).join(',')
    );
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sku-overrides.csv';
    a.click();
    URL.revokeObjectURL(url);
  }, [data]);

  const templateColumns = useMemo(
    () =>
      buildTemplateColumns(
        SKU_OVERRIDES_TEMPLATE.headers,
        SKU_OVERRIDES_TEMPLATE.rows,
        Object.fromEntries(
          SKU_OVERRIDES_TEMPLATE.headers.map((name) => [
            name,
            { required: SKU_OVERRIDE_REQUIRED[name], description: t(`csvTemplate.skuOverrides.${name}`) },
          ]),
        ),
      ),
    [t],
  );
  const templateProps = {
    fileName: SKU_OVERRIDES_TEMPLATE.fileName,
    columns: templateColumns,
    exampleRows: SKU_OVERRIDES_TEMPLATE.rows.map((row) => [...row]),
  };
  const productsExport = <ProductsCsvExportButton path="/store/sku-overrides/products.csv" fileName="sku-overrides-products.csv" size="medium" />;

  const overrides = data?.overrides || [];
  const total = data?.total || 0;

  return (
    <PageContainer maxWidth={1200}>
      <PageHeader
        title="SKU mapping"
        description="Match a catalog product by its UPC or manufacturer SKU, then send your own SKU and barcode to Shopify and BigCommerce."
        actions={
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <input
              type="file"
              accept=".csv,text/csv"
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />
            <CsvTemplateHint {...templateProps} compact size="medium" extraActions={productsExport} />
            <Button
              variant="outlined"
              startIcon={<CloudUpload />}
              onClick={() => fileInputRef.current?.click()}
            >
              Import CSV
            </Button>
            <Tooltip title={overrides.length === 0 ? t('store.skuCrosswalk.exportDisabled') : ''}>
              <span>
                <Button
                  variant="outlined"
                  startIcon={<Download />}
                  onClick={handleExportCsv}
                  disabled={overrides.length === 0}
                >
                  Export
                </Button>
              </span>
            </Tooltip>
            <Button variant="contained" startIcon={<Add />} onClick={openAddDialog}>
              Add mapping
            </Button>
          </Stack>
        }
      />

      <Box sx={{ mb: 2 }}>
        <CsvTemplateHint {...templateProps} explainerOnly intro={t('csvTemplate.skuOverrides.intro')} />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(error as Error).message}
        </Alert>
      )}

      <Card variant="outlined">
        <CardContent sx={{ pb: 0 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <TextField
              size="small"
              placeholder="Search by UPC, SKU, or product name..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              sx={{ width: 360 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip label={`${total} mapping${total !== 1 ? 's' : ''}`} size="small" />
              {total > 0 && (
                <Tooltip title="Delete all mappings">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => setDeleteAllDialogOpen(true)}
                  >
                    <DeleteSweep />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          </Stack>

          {isLoading ? (
            <Stack spacing={1}>
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} variant="rectangular" height={48} />
              ))}
            </Stack>
          ) : overrides.length === 0 ? (
            <EmptyState
              size="compact"
              title="No SKU mappings yet"
              description={
                <>
                  Download the template, or add a row for each product. Match by UPC or manufacturer SKU, then set your custom SKU, custom barcode, or both.
                </>
              }
              action={
                <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" useFlexGap>
                  <CsvTemplateHint {...templateProps} compact size="medium" extraActions={productsExport} />
                  <Button
                    variant="outlined"
                    startIcon={<CloudUpload />}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Import CSV
                  </Button>
                  <Button variant="contained" startIcon={<Add />} onClick={openAddDialog}>
                    Add mapping
                  </Button>
                </Stack>
              }
            />
          ) : (
            <>
              {isMobile ? (
                <Paper variant="outlined">
                  <Stack divider={<Divider />}>
                    {overrides.map((o) => (
                      <Box key={o.id} sx={{ p: 2 }}>
                        <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                          <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
                            {o.storeSku || '—'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                            {new Date(o.updatedAt).toLocaleDateString()}
                          </Typography>
                        </Stack>
                        <Typography variant="body2" fontFamily="monospace" color="text.secondary" sx={{ mt: 0.5 }}>
                          {o.upc ? `UPC ${o.upc}` : `SKU ${o.matchSku}`}
                          {o.storeBarcode ? ` · barcode ${o.storeBarcode}` : ''}
                        </Typography>
                        {o.productName && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {o.productName}
                          </Typography>
                        )}
                        <Box sx={{ mt: 1 }}>
                          <Typography component="span" sx={mobileLabelSx}>Canonical SKU</Typography>
                          <Typography variant="body2" fontFamily="monospace" color="text.secondary">
                            {o.canonicalSku || '—'}
                          </Typography>
                        </Box>
                        <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                          <Button size="small" startIcon={<Edit fontSize="small" />} onClick={() => openEditDialog(o)}>
                            Edit
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            startIcon={<Delete fontSize="small" />}
                            onClick={() => deleteMutation.mutate(o.matchKey || o.upc)}
                            disabled={deleteMutation.isPending}
                          >
                            Delete
                          </Button>
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                </Paper>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Match</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Custom SKU</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Custom barcode</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Product name</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Canonical SKU</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Updated</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {overrides.map((o) => (
                        <TableRow key={o.id} hover>
                          <TableCell>
                            <Typography variant="body2" fontFamily="monospace">
                              {o.upc || o.matchSku || '—'}
                            </Typography>
                            {o.upc && o.matchSku && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                SKU {o.matchSku}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              {o.storeSku || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontFamily="monospace">
                              {o.storeBarcode || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {o.productName || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontFamily="monospace" color="text.secondary">
                              {o.canonicalSku || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {new Date(o.updatedAt).toLocaleDateString()}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="Edit">
                              <IconButton size="small" onClick={() => openEditDialog(o)}>
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => deleteMutation.mutate(o.matchKey || o.upc)}
                                disabled={deleteMutation.isPending}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          resetCatalogSearch();
        }}
        maxWidth="sm"
        fullWidth
        fullScreen={fullScreenDialog}
        disableEnforceFocus
      >
        <DialogTitle>{editingExisting ? 'Edit mapping' : 'Add mapping'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {!editingExisting && (
              <Autocomplete
                fullWidth
                options={catalogOptions}
                value={matchedProduct}
                inputValue={catalogQuery}
                loading={catalogLoading}
                filterOptions={(options) => options}
                getOptionLabel={catalogLabel}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                noOptionsText={catalogQuery.trim().length < 2 ? 'Type at least 2 characters to search the catalog' : 'No matching products'}
                onChange={(_event, value) => {
                  applyCatalogProduct(value);
                  setCatalogQuery(value ? catalogLabel(value) : '');
                }}
                onInputChange={(_event, value, reason) => {
                  if (reason === 'reset') return;
                  setCatalogQuery(value);
                }}
                slotProps={{ popper: { sx: { zIndex: (muiTheme) => muiTheme.zIndex.modal + 1 } } }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Find a product"
                    placeholder="Search by name, model, SKU or barcode"
                    helperText="Fills the barcode, manufacturer SKU, and name. You still enter the SKU or barcode your store writes."
                  />
                )}
              />
            )}
            <TextField
              label="UPC / EAN / GTIN"
              value={editUpc}
              onChange={(e) => setEditUpc(e.target.value)}
              onBlur={() => setUpcTouched(true)}
              disabled={editingExisting}
              fullWidth
              placeholder="e.g. 027242923799"
              error={Boolean(upcProblem)}
              helperText={upcProblem ?? (editingExisting ? 'Match key cannot be changed' : 'Manufacturer barcode. Leave blank if the product has no UPC and you fill in manufacturer SKU.')}
            />
            <TextField
              label="Manufacturer SKU"
              value={editMatchSku}
              onChange={(e) => setEditMatchSku(e.target.value)}
              disabled={editingExisting}
              fullWidth
              placeholder="e.g. ILCE-7M4"
              helperText="The manufacturer's SKU. Used to match products that have no UPC, such as warranties."
            />
            <TextField
              label="Custom SKU"
              value={editStoreSku}
              onChange={(e) => setEditStoreSku(e.target.value)}
              fullWidth
              placeholder="e.g. CAM-123-00045"
              helperText="Written to Shopify and BigCommerce as the SKU. Leave blank to keep the manufacturer SKU."
            />
            <TextField
              label="Custom barcode"
              value={editStoreBarcode}
              onChange={(e) => setEditStoreBarcode(e.target.value)}
              fullWidth
              placeholder="e.g. CAM-123-00045"
              helperText="Written as the barcode. Use this when the product has no GTIN and your store SKU is the barcode."
            />
            <TextField
              label="Product name (optional)"
              value={editProductName}
              onChange={(e) => setEditProductName(e.target.value)}
              fullWidth
              placeholder="e.g. Sony Alpha 7 IV"
              helperText="Helps identify the product in this list"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={
              (!editUpc.trim() && !editMatchSku.trim())
              || (!editStoreSku.trim() && !editStoreBarcode.trim())
              || Boolean(currentUpcProblem)
              || upsertMutation.isPending
            }
            startIcon={upsertMutation.isPending ? <CircularProgress size={16} /> : undefined}
          >
            {editingExisting ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import CSV Dialog */}
      <Dialog
        open={importDialogOpen}
        onClose={() => {
          setImportDialogOpen(false);
          setImportFile(null);
          setImportPreview(null);
          setImportResult(null);
        }}
        maxWidth="md"
        fullWidth
        fullScreen={fullScreenDialog}
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <FilePresent />
            <span>Import SKU mapping</span>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {importLoading && <LoadingState />}

          {importPreview && !importResult && !importLoading && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                Found <strong>{importPreview.totalRows}</strong> valid row{importPreview.totalRows !== 1 ? 's' : ''} in{' '}
                <strong>{importFile?.name}</strong>
                {importPreview.errors.length > 0 && (
                  <> with <strong>{importPreview.errors.length}</strong> error{importPreview.errors.length !== 1 ? 's' : ''}</>
                )}
              </Alert>

              {(importPreview.blank ?? 0) > 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>{t('csvTemplate.skuOverrides.blankRows', { count: importPreview.blank })}</Alert>
              )}
              {importPreview.errors.length > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {importPreview.errors.slice(0, 5).map((e, i) => (
                    <div key={i}>Row {e.row}: {e.error}</div>
                  ))}
                  {importPreview.errors.length > 5 && (
                    <div>...and {importPreview.errors.length - 5} more errors</div>
                  )}
                </Alert>
              )}

              {importPreview.preview.length > 0 && (
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Match</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Custom SKU</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Custom barcode</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Product name</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {importPreview.preview.map((row, i) => (
                        <TableRow key={i}>
                          <TableCell>
                            <Typography variant="body2" fontFamily="monospace">{row.upc || row.matchSku || '—'}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>{row.storeSku || '—'}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontFamily="monospace">{row.storeBarcode || '—'}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">{row.productName || '—'}</Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                      {importPreview.totalRows > 20 && (
                        <TableRow>
                          <TableCell colSpan={4} align="center">
                            <Typography variant="caption" color="text.secondary">
                              Showing first 20 of {importPreview.totalRows} rows
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}

          {importResult && !importLoading && (
            <>
              <Alert severity={importResult.errors.length > 0 ? 'warning' : 'success'} sx={{ mb: 2 }}>
                Import complete: <strong>{importResult.created}</strong> created,{' '}
                <strong>{importResult.updated}</strong> updated,{' '}
                <strong>{importResult.skipped}</strong> unchanged
                {(importResult.blank ?? 0) > 0 && (
                  <>, <strong>{importResult.blank}</strong> left blank</>
                )}
                {importResult.errors.length > 0 && (
                  <>, <strong>{importResult.errors.length}</strong> error{importResult.errors.length !== 1 ? 's' : ''}</>
                )}
              </Alert>
              {importResult.errors.length > 0 && (
                <Alert severity="error">
                  {importResult.errors.slice(0, 10).map((e, i) => (
                    <div key={i}>Row {e.row}: {e.error}</div>
                  ))}
                  {importResult.errors.length > 10 && (
                    <div>...and {importResult.errors.length - 10} more errors</div>
                  )}
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setImportDialogOpen(false);
              setImportFile(null);
              setImportPreview(null);
              setImportResult(null);
            }}
          >
            {importResult ? 'Close' : 'Cancel'}
          </Button>
          {importPreview && !importResult && (
            <Button
              variant="contained"
              onClick={handleImportConfirm}
              disabled={importLoading || importPreview.totalRows === 0}
              startIcon={importLoading ? <CircularProgress size={16} /> : <CloudUpload />}
            >
              Import {importPreview.totalRows} mapping{importPreview.totalRows !== 1 ? 's' : ''}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete All Confirmation */}
      <Dialog open={deleteAllDialogOpen} onClose={() => setDeleteAllDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete all SKU mappings?</DialogTitle>
        <DialogContent>
          <Typography>
            This will permanently remove all <strong>{total}</strong> SKU mapping{total !== 1 ? 's' : ''} for your store.
            Your next sync will use the manufacturer's SKU and barcode until you re-import.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteAllDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => deleteAllMutation.mutate()}
            disabled={deleteAllMutation.isPending}
            startIcon={deleteAllMutation.isPending ? <CircularProgress size={16} /> : <DeleteSweep />}
          >
            Delete all
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

/**
 * Flag `sku_crosswalk`: the many-to-one store SKU and barcode mapping replaces the
 * one-row-per-UPC override table. Flag off renders the page exactly as before.
 */
export const SkuOverridesPage: React.FC = () => {
  const { enabled, isLoading } = useOrgFeatureFlag('sku_crosswalk');
  if (isLoading) return <LoadingState />;
  return enabled ? <SkuCrosswalkPanel /> : <LegacySkuOverridesPage />;
};

export default SkuOverridesPage;
